import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getAuthToken } from '../utils/auth';
import { API_BASE_URL } from '../config/api';

/**
 * Start processing documents for a collection
 *
 * @param {string} collectionId - The collection ID
 * @param {string[]} documentIds - Array of document IDs to process
 * @param {number} estimatedItems - Optional estimated item count for ETA (linesheet-only)
 * @returns {Promise<Object>} Processing start response
 */
const processDocuments = async (collectionId, documentIds, estimatedItems = null) => {
  const token = await getAuthToken();
  const body = { document_ids: documentIds };
  if (estimatedItems) {
    body.estimated_items = estimatedItems;
  }
  const response = await fetch(`${API_BASE_URL}/collections/${collectionId}/documents/process`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to start document processing');
  }
  
  return await response.json();
};

/**
 * Cancel document processing for a collection
 * 
 * @param {string} collectionId - The collection ID
 * @returns {Promise<Object>} Cancellation response
 */
const cancelDocumentProcessing = async (collectionId) => {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE_URL}/collections/${collectionId}/documents/process/cancel`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to cancel document processing');
  }
  
  return await response.json();
};

/**
 * Mark document processing results as stale
 * 
 * @param {string} collectionId - The collection ID
 * @returns {Promise<Object>} Mark stale response
 */
const markDocumentsStale = async (collectionId) => {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE_URL}/collections/${collectionId}/documents/mark-stale`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to mark documents as stale');
  }
  
  return await response.json();
};

/**
 * Hook for starting document processing
 * 
 * @returns {Object} Mutation object with mutate function and state
 * 
 * @example
 * const processDocsMutation = useProcessDocuments();
 * 
 * processDocsMutation.mutate(
 *   { collectionId: '123', documentIds: ['doc1', 'doc2'] },
 *   {
 *     onSuccess: () => console.log('Processing started'),
 *     onError: (error) => console.error(error.message)
 *   }
 * );
 */
export const useProcessDocuments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ collectionId, documentIds, estimatedItems }) => processDocuments(collectionId, documentIds, estimatedItems),
    onMutate: async (variables) => {
      console.log('[useProcessDocuments] onMutate - optimistically setting status to processing');
      
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['processingStatus', variables.collectionId] });
      
      // Snapshot the previous value
      const previousStatus = queryClient.getQueryData(['processingStatus', variables.collectionId]);
      
      // Optimistically update to 'processing' status
      queryClient.setQueryData(['processingStatus', variables.collectionId], (old) => ({
        ...old,
        document_processing: {
          status: 'processing',
          current_phase: 'Starting...',
          progress: { phase: 0, total_phases: 6, percentage: 0 },
          error: null
        }
      }));
      
      // Return context with the previous value
      return { previousStatus };
    },
    onSuccess: (data, variables) => {
      console.log('[useProcessDocuments] onSuccess - updating cache with server response', data);
      
      // Update cache with the actual processing status from server response
      if (data.processing_status) {
        queryClient.setQueryData(['processingStatus', variables.collectionId], (old) => ({
          ...old,
          ...data.processing_status
        }));
      }
      
      // Invalidate documents list (will be updated when processing completes)
      queryClient.invalidateQueries({ queryKey: ['collectionDocuments', variables.collectionId] });
    },
    onError: (error, variables, context) => {
      console.log('[useProcessDocuments] onError - rolling back optimistic update', error);
      
      // Rollback to previous status on error
      if (context?.previousStatus) {
        queryClient.setQueryData(['processingStatus', variables.collectionId], context.previousStatus);
      }
    },
  });
};

/**
 * Hook for cancelling document processing
 * 
 * @returns {Object} Mutation object with mutate function and state
 * 
 * @example
 * const cancelMutation = useCancelDocumentProcessing();
 * 
 * cancelMutation.mutate(
 *   { collectionId: '123' },
 *   {
 *     onSuccess: () => console.log('Processing cancelled'),
 *     onError: (error) => console.error(error.message)
 *   }
 * );
 */
export const useCancelDocumentProcessing = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ collectionId }) => cancelDocumentProcessing(collectionId),
    onSuccess: (data, variables) => {
      // Invalidate processing status to reflect cancellation
      queryClient.invalidateQueries({ queryKey: ['processingStatus', variables.collectionId] });
    },
  });
};

/**
 * Hook for marking document processing as stale
 * 
 * Called when staged documents change after processing has completed.
 * This enables the "Process Documents" button without deleting any data.
 * 
 * @returns {Object} Mutation object with mutate function and state
 * 
 * @example
 * const markStaleMutation = useMarkDocumentsStale();
 * 
 * markStaleMutation.mutate(
 *   { collectionId: '123' },
 *   {
 *     onSuccess: () => console.log('Marked as stale'),
 *     onError: (error) => console.error(error.message)
 *   }
 * );
 */
export const useMarkDocumentsStale = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ collectionId }) => markDocumentsStale(collectionId),
    onSuccess: (data, variables) => {
      // Update processing status to reflect stale state
      queryClient.setQueryData(['processingStatus', variables.collectionId], (old) => ({
        ...old,
        document_processing: {
          ...old?.document_processing,
          is_stale: true
        }
      }));
    },
  });
};
